import { useState, useRef, useEffect } from 'react'
import { ChevronDown, Headphones } from 'lucide-react'
import { cn } from '@/utils/cn'
import { useAuth } from '@/hooks/useAuth'
import { auth } from '@/firebase'
import { API_BASE_URL } from '@/config'
import { useTranslation } from '@/i18n'

type AgentStatus = 'online' | 'away' | 'offline'

interface AgentStatusBarProps {
  currentChats: number
  maxConcurrent: number
  connected: boolean
}

export default function AgentStatusBar({ currentChats, maxConcurrent, connected }: AgentStatusBarProps) {
  const { user } = useAuth()
  const { t } = useTranslation()
  const [status, setStatus] = useState<AgentStatus>('online')
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const statusConfig: Record<AgentStatus, { label: string; dot: string }> = {
    online: { label: t('agent.online'), dot: 'bg-green-500' },
    away: { label: t('agent.away'), dot: 'bg-yellow-500' },
    offline: { label: t('agent.offline'), dot: 'bg-gray-500' },
  }

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  async function updateStatus(newStatus: AgentStatus) {
    setStatus(newStatus)
    setDropdownOpen(false)
    try {
      const token = await auth.currentUser?.getIdToken()
      const apiBase = API_BASE_URL
      await fetch(`${apiBase}/agents/me/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus }),
      })
    } catch {
      // Status will sync on next WS reconnect
    }
  }

  const cfg = statusConfig[status]

  return (
    <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-200">
      <div className="flex items-center gap-3">
        <Headphones className="h-5 w-5 text-indigo-500" />
        <span className="text-sm font-medium text-gray-900">{t('agent.workspace')}</span>
        {!connected && (
          <span className="text-xs text-red-500 bg-red-50 px-2 py-0.5 rounded-full">{t('agent.disconnected')}</span>
        )}
      </div>

      <div className="flex items-center gap-4">
        <span className="text-xs text-gray-500">
          {t('agent.currentChats')} <span className="font-medium text-gray-900">{currentChats}</span> / {maxConcurrent}
        </span>

        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-semibold text-indigo-600">
              {(user?.displayName || user?.email || '?')[0].toUpperCase()}
            </div>
            <span className="text-sm text-gray-700">{user?.displayName || user?.email || t('agent.agent')}</span>
            <span className={cn('w-2 h-2 rounded-full', cfg.dot)} />
            <span className="text-xs text-gray-500">{cfg.label}</span>
            <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1">
              {(Object.keys(statusConfig) as AgentStatus[]).map(s => (
                <button
                  key={s}
                  onClick={() => updateStatus(s)}
                  className={cn('w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50', status === s && 'bg-gray-50')}
                >
                  <span className={cn('w-2 h-2 rounded-full', statusConfig[s].dot)} />
                  {statusConfig[s].label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
